// H2HPage.js — /head-to-head/:pair (e.g. /head-to-head/arsenal-vs-chelsea):
// every Premier League meeting of two clubs. The record banner, a computed
// lede (the current run, the last meeting), the next meeting when it's in the
// coming round (with its Scorigami Watch odds), home and away records,
// every meeting, biggest wins each way, highest-scoring meetings, most common
// results, the longest runs and the pair's scoreline grid. Data: ?h2h=A,B.
// Pairs that met fewer than H2H_INDEX_MIN times are noindex (one season is
// too thin for search), but still work and are linked from the club pages.

import React, { useMemo, useState } from "react";
import { Link as RouterLink, Navigate, useLocation, useParams } from "react-router-dom";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import NotFoundPage from "./NotFoundPage";
import ScoreGrid from "./components/ScoreGrid";
import { MatchRows } from "./components/StatBits";
import { CtaRow, H2, InternalLink, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { formatDate } from "./components/heatmap/utils";
import { useSeededQuery } from "./components/scorelines/data";
import { clubInfo, clubPath, possessive, seasonText } from "./components/clubs/data";
import { seasonPath } from "./components/clubs/seasons";
import { H2H_INDEX_MIN, h2hPath, h2hQuery, h2hStats, parsePair } from "./components/clubs/h2h";
import { oddsText } from "./WatchPage";

const SITE_URL = "https://premierleaguescorigami.com";
const SHOW_MEETINGS = 20;

export default function H2HRoute() {
  const { pair } = useParams();
  const { pathname } = useLocation();
  const teams = parsePair(pair);
  if (!teams) return <NotFoundPage />;
  const canonical = h2hPath(...teams);
  if (pathname !== canonical) return <Navigate to={canonical} replace />;
  // The URL is canonical by now, so its order is the page's order.
  const [a, b] = teams;
  return <H2HPage key={canonical} a={a} b={b} path={canonical} />;
}

function H2HPage({ a, b, path }) {
  const A = clubInfo(a);
  const B = clubInfo(b);
  const resp = useSeededQuery(h2hQuery(a, b));
  const watch = useSeededQuery("watch=1");
  const stats = useMemo(() => (resp.data ? h2hStats(a, b, resp.data.items || []) : null), [resp.data, a, b]);
  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Head to head", item: `${SITE_URL}/head-to-head` },
      { "@type": "ListItem", position: 3, name: `${A.name} vs ${B.name}`, item: `${SITE_URL}${path}` },
    ],
  };
  const title =
    [`${A.name} vs ${B.name} head to head: Premier League record`, `${A.short} vs ${B.short} head to head: Premier League record`].find((t) => t.length <= 65) ||
    `${A.short} vs ${B.short} head to head`;
  const h1 = `${A.name} vs ${B.name} head to head`;
  const eyebrow = <InternalLink to="/head-to-head">Head to head</InternalLink>;

  if (!stats) {
    return (
      <ProsePage wide>
        <SEO title={title} description={`${A.name} vs ${B.name}: every Premier League meeting, the head-to-head record, biggest wins and the scoreline grid.`} path={path} jsonLd={breadcrumbs} />
        <PageHeader eyebrow={eyebrow} title={h1} />
        {resp.error ? (
          <P>This head to head couldn't be loaded right now. Please try again shortly.</P>
        ) : (
          Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={44} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />)
        )}
      </ProsePage>
    );
  }

  if (!stats.played) {
    return (
      <ProsePage>
        <SEO title={title} description={`${A.name} and ${B.name} have never met in the Premier League. See each club's record and every other head to head.`} path={path} noindex />
        <PageHeader eyebrow={eyebrow} title={h1} lede={`${A.name} and ${B.name} have never met in the Premier League.`} />
        <CtaRow
          links={[
            { to: clubPath(a), label: A.name, primary: true },
            { to: clubPath(b), label: B.name },
            { to: "/head-to-head", label: "Every head to head" },
          ]}
        />
      </ProsePage>
    );
  }

  const s = stats;
  const n = s.played;
  const firstSeason = seasonText(s.first.season);
  const lastText = `${clubInfo(s.last.homeTeam).short} ${s.last.hs}–${s.last.as} ${clubInfo(s.last.awayTeam).short}`;
  const run = (club, r) =>
    r.win >= 3 ? `${club.name} have won the last ${r.win} meetings. ` : r.unbeaten >= 4 ? `${club.name} are unbeaten in the last ${r.unbeaten} meetings. ` : "";
  const lede =
    `${A.name} and ${B.name} have met ${n === 2 ? "twice" : `${n} times`} in the Premier League since ${firstSeason}. ` +
    `${recordSentence(A, B, s)} ` +
    run(A, s.runsA) +
    run(B, s.runsB) +
    `The last meeting was ${lastText} on ${formatDate(s.last.utcDate)}.`;
  const description =
    [
      `${A.name} vs ${B.name}: ${n} Premier League meetings, ${A.short} ${s.a} wins, ${B.short} ${s.b}, ${s.d} draws. Every result, biggest wins and the scoreline grid.`,
      `${A.short} vs ${B.short}: ${n} Premier League meetings, ${A.short} ${s.a} wins, ${B.short} ${s.b}, ${s.d} draws. Every result and biggest wins.`,
    ].find((d) => d.length <= 160) || `${A.short} vs ${B.short}: every Premier League meeting and the head-to-head record.`;

  // The next meeting, if it's in the round Scorigami Watch covers.
  const next = (watch.data?.fixtures || []).find((f) => (f.home === a && f.away === b) || (f.home === b && f.away === a));

  const matchText = (m) => `${clubInfo(m.homeTeam).short} ${m.hs}–${m.as} ${clubInfo(m.awayTeam).short}`;
  const row = (m, badge) => ({ date: formatDate(m.utcDate), text: matchText(m), badge, to: `/scorelines/${s.scoreline(m)}` });

  return (
    <ProsePage wide>
      <SEO title={title} description={description} path={path} jsonLd={breadcrumbs} noindex={n < H2H_INDEX_MIN} />
      <Box sx={{ maxWidth: 820 }}>
        <PageHeader eyebrow={eyebrow} title={h1} lede={lede} />
      </Box>

      <RecordBanner A={A} B={B} s={s} crests={crestsOf(s.meetings)} />

      {next && (
        <Box sx={{ maxWidth: 820, mb: 3, p: 2, borderRadius: 3, background: "rgba(111,93,245,0.14)", border: "1px solid rgba(111,93,245,0.4)" }}>
          <Typography sx={{ color: BRAND.cyan, fontSize: 11.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>Next meeting</Typography>
          <Typography sx={{ color: "#fff", fontSize: 16, fontWeight: 700, mt: 0.5 }}>
            {clubInfo(next.home).short} v {clubInfo(next.away).short},{" "}
            {new Date(next.utcDate).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
          </Typography>
          <Typography sx={{ color: "#c9ccd6", fontSize: 14, mt: 0.5 }}>
            The odds of a result the Premier League has never seen: {oddsText(next.pNew)}.{" "}
            <InternalLink to="/watch">Scorigami Watch →</InternalLink>
          </Typography>
        </Box>
      )}

      <Box sx={{ maxWidth: 820 }}>
        <H2 id="venues">Home and away</H2>
        <VenueTable A={A} B={B} s={s} />

        <H2 id="meetings">Every meeting</H2>
        <Meetings meetings={[...s.meetings].reverse()} row={row} />

        <H2 id="biggest-wins">Biggest wins</H2>
        {[
          [A, s.biggestA],
          [B, s.biggestB],
        ].map(([club, list]) => (
          <Box key={club.team}>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 15.5, mt: 1.5, mb: 0.5 }}>{club.name}</Typography>
            {list.length ? (
              <MatchRows rows={list.map((m) => row(m, `${Math.abs(m.ag - m.bg)}-goal win`))} />
            ) : (
              <P>{club.name} have never beaten {club.team === a ? B.name : A.name} in the Premier League.</P>
            )}
          </Box>
        ))}

        <H2 id="highest-scoring">Highest-scoring meetings</H2>
        <MatchRows rows={s.highest.map((m) => row(m, `${m.hs + m.as} goals`))} />

        <AdSlot position="article" />

        <H2 id="most-common">Most common results</H2>
        <MatchRows
          rows={s.common.map((c, i) => ({
            date: `#${i + 1}`,
            text: c.ag === c.bg ? `${c.ag}–${c.bg} draw` : `${c.ag > c.bg ? A.short : B.short} ${Math.max(c.ag, c.bg)}–${Math.min(c.ag, c.bg)} win`,
            badge: `${c.n} time${c.n === 1 ? "" : "s"}`,
            to: `/scorelines/${Math.max(c.ag, c.bg)}-${Math.min(c.ag, c.bg)}`,
          }))}
        />

        <H2 id="runs">Runs</H2>
        <P>
          {possessive(A.name)} longest winning run in this fixture is {s.runsA.bestWin} and {possessive(B.name)} is {s.runsB.bestWin}. The longest unbeaten
          runs are {s.runsA.bestUnbeaten} for {A.name} and {s.runsB.bestUnbeaten} for {B.name}.
        </P>

        <H2 id="grid">The scoreline grid</H2>
        <P>
          Every meeting from {possessive(A.name)} side: {A.short} goals along the bottom, {B.short} goals up the side. Select a square for that
          scoreline's page.
        </P>
      </Box>
      <ScoreGrid
        counts={s.cells}
        maxGoals={s.maxGoals}
        xLabel={`${A.short} goals`}
        yLabel={`${B.short} goals`}
        labelFor={(x, y, c) => `${A.short} ${x}–${y} ${B.short}: ${c} time${c === 1 ? "" : "s"}`}
      />

      <CtaRow
        links={[
          { to: clubPath(a), label: A.name, primary: true },
          { to: clubPath(b), label: B.name },
          { to: "/head-to-head", label: "Every head to head" },
        ]}
      />
    </ProsePage>
  );
}

// "Arsenal have won 29, Chelsea 20, and 20 were drawn." Reads right when a
// side has never won, or nothing was drawn.
function recordSentence(A, B, s) {
  const times = (k) => (k === 1 ? "once" : `${k}`);
  const drawn = s.d === 0 ? "none were drawn" : `${s.d} ${s.d === 1 ? "was" : "were"} drawn`;
  if (s.a === 0 && s.b === 0) return s.d === 1 ? "Their only meeting was drawn." : "Every one of them was drawn.";
  if (s.a === 0) return `${B.name} have won ${times(s.b)} and ${drawn}; ${A.name} have never won.`;
  if (s.b === 0) return `${A.name} have won ${times(s.a)} and ${drawn}; ${B.name} have never won.`;
  return `${A.name} have won ${times(s.a)}, ${B.name} ${times(s.b)}, and ${drawn}.`;
}

// Each club's most recent crest, from the meeting rows.
function crestsOf(meetings) {
  const crests = new Map();
  for (const m of meetings) {
    if (m.homeTeamImage) crests.set(m.homeTeam, m.homeTeamImage);
    if (m.awayTeamImage) crests.set(m.awayTeam, m.awayTeamImage);
  }
  return crests;
}

function RecordBanner({ A, B, s, crests }) {
  const col = (value, label, color) => (
    <Box sx={{ textAlign: "center", minWidth: 0 }}>
      <Typography sx={{ color, fontSize: { xs: 34, sm: 48 }, fontWeight: 900, lineHeight: 1, fontFeatureSettings: '"tnum"' }}>{value}</Typography>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", mt: 0.75 }}>{label}</Typography>
    </Box>
  );
  const club = (c) => (
    <Box component={RouterLink} to={clubPath(c.team)} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, textDecoration: "none", color: "#fff", minWidth: 0 }}>
      {crests.get(c.team) ? (
        <Box component="img" src={crests.get(c.team)} alt={`${c.name} crest`} sx={{ width: 56, height: 56, objectFit: "contain" }} />
      ) : (
        <Box sx={{ width: 56, height: 56 }} />
      )}
      <Typography sx={{ fontWeight: 800, fontSize: { xs: 13, sm: 15 }, textAlign: "center" }}>{c.short}</Typography>
    </Box>
  );
  const pct = (k) => `${(100 * k) / s.played}%`;
  return (
    <Box sx={{ maxWidth: 820, mb: 3, p: { xs: 2, sm: 2.5 }, borderRadius: 3, background: "linear-gradient(180deg, rgba(25,25,32,0.85) 0%, rgba(18,18,24,0.85) 100%)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr repeat(3, minmax(0, 1fr)) 1fr", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
        {club(A)}
        {col(s.a, `${A.short} wins`, BRAND.cyan)}
        {col(s.d, "Draws", "#fff")}
        {col(s.b, `${B.short} wins`, "#ff4da6")}
        {club(B)}
      </Box>
      <Box sx={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", mt: 2, background: "rgba(255,255,255,0.08)" }} aria-hidden>
        <Box sx={{ width: pct(s.a), background: BRAND.cyan }} />
        <Box sx={{ width: pct(s.d), background: "rgba(255,255,255,0.35)" }} />
        <Box sx={{ width: pct(s.b), background: "#ff4da6" }} />
      </Box>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 13, textAlign: "center", mt: 1.25, fontFeatureSettings: '"tnum"' }}>
        {s.played} meetings · goals {A.short} {s.ag}–{s.bg} {B.short}
      </Typography>
    </Box>
  );
}

function VenueTable({ A, B, s }) {
  const rows = [
    [`At ${A.short}`, s.atA],
    [`At ${B.short}`, s.atB],
  ];
  return (
    <Box sx={{ overflowX: "auto", mb: 2 }}>
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontFeatureSettings: '"tnum"',
          "& th": { textAlign: "right", color: BRAND.textSecondary, fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", py: 1, px: 1, borderBottom: "1px solid rgba(255,255,255,0.12)" },
          "& td": { textAlign: "right", py: 1, px: 1, borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#d7dae3", fontSize: 14.5, whiteSpace: "nowrap" },
          "& th:first-of-type, & td:first-of-type": { textAlign: "left", pl: 0, color: "#fff", fontWeight: 700 },
        }}
      >
        <thead>
          <tr>
            <th>Venue</th>
            <th>P</th>
            <th>{A.short} W–D–L</th>
            <th>Goals</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, v]) => (
            <tr key={label}>
              <td>{label}</td>
              <td>{v.played}</td>
              <td>
                {v.a}–{v.d}–{v.b}
              </td>
              <td>
                {v.ag}–{v.bg}
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function Meetings({ meetings, row }) {
  const [all, setAll] = useState(false);
  const shown = all ? meetings : meetings.slice(0, SHOW_MEETINGS);
  return (
    <>
      <MatchRows rows={shown.map((m) => ({ ...row(m, null), badge: seasonText(m.season), badgeTo: seasonPath(m.season) }))} />
      {meetings.length > SHOW_MEETINGS && !all && (
        <Button onClick={() => setAll(true)} sx={{ color: BRAND.cyan, fontWeight: 700, textTransform: "none", mb: 2 }}>
          Show all {meetings.length} meetings
        </Button>
      )}
    </>
  );
}
